import { Injectable } from '@angular/core';
import { TourInformation } from '../interfaces/tour-information';
import { map } from 'rxjs/operators';
import { Search } from '../interfaces/search';
import { TourService } from './tour.service';

@Injectable({
  providedIn: 'root'
})
export class FilterService {

  public filterOptions: string[] = [
    'type',
    'location',
    'owners',
    //'difficulty', Temporary removed
    'price',
    'departures',
    'valid_ticket_types',
  ]


  constructor(
    private tourService: TourService,
  ) { }


  /**
   * Get filter data from API provider and map filter options
   * @returns Observable
   **/
  public getFilterData() {
    // Get all tours from API provider and map filter options
    return this.tourService.getToursInventroy().pipe(
      map((tourData: any) => {
        let tours = tourData.results
        return {
          filterOptions: this.buildFilterOptionsFromTours(tours),
          filterData: tours
        }
      })
    )
  }


  /**
   * Filter data by fields
   * @param items
   * @param fields
   * @returns Tour[]
   **/
  public filterDataByFields(items: TourInformation[], fields: Search) {

    let filteredItems: TourInformation[] = items
    let dateFrom: Date
    let dateTo: Date

    // Set date objects to ensure both from and to-dates are available on check
    if (fields.dateFrom) {
      dateFrom = new Date(fields.dateFrom)
    }
    if (fields.dateTo) {
      dateTo = new Date(fields.dateTo)
    }

    // Set price values to ensure both from and to-prices are available on check
    const priceFrom: number = fields.priceFrom ? fields.priceFrom : 0
    const priceTo: number = fields.priceTo ? fields.priceTo : 999999

    // Chech all fields for values and filter items by rules
    Object.entries(fields).forEach(([field, value]) => {
      if (!value || value.length === 0) { return }

      filteredItems = filteredItems.filter((tour: TourInformation) => {
        switch (field) {
          case 'priceFrom':
          case 'priceTo':
            const price = Number(tour.price)
            return (priceFrom <= price && priceTo >= price) ? true : false

          case 'dateFrom':
          case 'dateTo':
            let departureDateValid: boolean = false
            tour.availableDepartures = []

            tour.departures.forEach((departure: any) => {
              const departureDate = new Date(departure.isoDateTime)

              if (dateFrom <= departureDate && dateTo >= departureDate) {
                departureDateValid = true
              }
              else if (dateFrom <= departureDate && dateTo === undefined) {
                departureDateValid = true
              }
              else if (dateTo >= departureDate && dateFrom === undefined) {
                departureDateValid = true
              }

              // Push available departure dates to tour for displaying
              if (departureDateValid) {
                if (tour.availableDepartures.length < 1) {
                  tour.availableDepartures.push(departure)
                }
              }
            })

            return departureDateValid

          case 'location':
          case 'type':
            return this.checkObjectSlugValue(tour[field], value)

          case 'difficulty':
            return value.includes(tour[field]) ? true : false

          case 'qty':
            let departureValid: boolean = false
            tour.departures.forEach((departure: any) => {
              if (value <= departure.qty) {
                departureValid = true
              }
            })
            return departureValid

          case 'owners':
            if (Array.isArray(value)) {
              return value.some((val: string) => this.checkArrayForObjectSlugValue(tour[field], val));
            } else {
              return this.checkArrayForObjectSlugValue(tour[field], value);
            }

          case 'ticketTypes':
            return value.every((val: string) => tour['valid_ticket_types'].some((ticketType: any) => ticketType.slug === val))

          case 'text':
            const searchterm = value.toString()
            const target = tour.id + ' ' + tour.location?.label + ' ' + tour.owners[0]?.name + ' ' + tour.post_title + ' ' + tour.post_excerpt

            if (target.toLowerCase().includes(searchterm.toLowerCase())) {
              return true
            }

            return false

          default:
            return false
        }
      })
    })

    return filteredItems
  }


  /**
   * Build filter options from tour data
   * @param key
   * @param value
   **/
  public formatParamFilterOption(key: string, value: any) {
    if (!value) { return }

    switch (key) {
      case 'dateFrom':
      case 'dateTo':
        return new Date(value)

      case 'priceFrom':
      case 'priceTo':
        return Number(value)

      case 'qty':
        return Number(value)

      case 'difficulty':
        return value.split(',').map((val: string) => Number(val))

      default:
        return value.split(',')
    }
  }


  /**
   * Build filter options from tour data
   * @param tourData 
   * @returns object
   */
  public buildFilterOptionsFromTours(tourData: TourInformation[], filterOptions: string[] = this.filterOptions) {

    let filterOptionsValues: any = {}

    filterOptions.forEach((filteroption: string) => {
      tourData.forEach((tour: TourInformation) => {

        // Break on empty or faulty data
        if (
          !tour[filteroption]
          || tour[filteroption].length === 0
          || tour[filteroption].slug === null) {
          return
        }

        // Create filter options array if not exists
        if (!filterOptionsValues[filteroption]) {
          filterOptionsValues[filteroption] = []
        }

        // Push filter option values to array
        if (Array.isArray(tour[filteroption])) {
          if (tour[filteroption].length > 0) {
            tour[filteroption].forEach((itemoption: any) => {
              // We use slug as a unique identifier
              if (itemoption.id) {
                delete itemoption.id;
              }
              filterOptionsValues[filteroption].push(itemoption)
            })
          }
        }
        else {
          filterOptionsValues[filteroption].push(tour[filteroption])
        }
      })

      // Clean up filter options if set
      if (filterOptionsValues[filteroption]) {

        // Group price filter options
        if (filteroption === 'price') {
          filterOptionsValues[filteroption] = this.makeRange(filterOptionsValues[filteroption])
        }

        // Remove duplicates without reference to the object keys
        let newFilterOptionsValues: any = []
        let keys: any[] = []

        filterOptionsValues[filteroption].forEach(function (option: any) {

          let key = Object.values(option)[0]

          if (typeof option == 'number') {
            key = option
          }

          if (!keys.includes(key)) {
            keys.push(key)
            newFilterOptionsValues.push(option)
          }
        })

        // Update the filter options values with the unique values (sortet numeric as default)
        filterOptionsValues[filteroption] = newFilterOptionsValues.sort((a: number, b: number) => {
          return a - b;
        })

        // Quirk: Firefox seems to be sorting this wrongly, which causes the array to be flipped
        if(filteroption !== 'price') {
          // Sort filter options by slug
          filterOptionsValues[filteroption] = filterOptionsValues[filteroption].sort((a: any, b: any) => {
            return (a.slug < b.slug) ? -1 : 1
          })
        }
      }
    })

    return filterOptionsValues
  }


  /**
   * Make a pretty range of two values
   * @param range
   * @param numberSize
   * @returns object[]
   **/
  private makeRange(range: number[], numberSize: number = 100) {

    const min = Math.min(...range)
    const max = Math.max(...range)
    
    const intervalSize = (max - min)

    const start = Math.floor(min / numberSize) * numberSize
    const end = Math.ceil((start + intervalSize) / numberSize) * numberSize

    return [start, end]
  }


  /**
   * Check if a value is found in an object slug
   * @param object 
   * @param value 
   * @returns boolean
   */
  private checkObjectSlugValue(object: any, value: string) {
    return object.slug && value.includes(object.slug) ? true : false
  }


  /**
   * Check if a value is found in an array of object slugs
   * @param array
   * @param value
   * @returns boolean
   **/
  private checkArrayForObjectSlugValue(array: any[], value: string): boolean {
      if (!array || !Array.isArray(array)) {
          return false;
      }
      return array.some(item => item.slug === value);
  }
}
